/**
 * School Settings API Hook
 *
 * Fetches configurable swim levels, locations, and program types
 * from the school_settings API. Used by forms and filter dropdowns.
 */

import { useQuery } from "@tanstack/react-query"

import { api } from "@/services/api"
import { unwrapStandardResponse } from "@/utils/apiResponse"

export interface SchoolSetting {
  id: string
  category: string
  value: string
  label: string
  sort_order: number
  is_active: boolean
}

interface SchoolSettingListResponse {
  items: SchoolSetting[]
  total: number
}

export function useSchoolSettings(category?: string) {
  const { data, ...rest } = useQuery({
    queryKey: ["school-settings", category],
    queryFn: async () => {
      const url = category
        ? `/api/v1/school-settings/${category}`
        : "/api/v1/school-settings/"
      const response = await api.instance.get(url)
      return unwrapStandardResponse(response.data) as SchoolSettingListResponse
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - these change rarely
  })

  const items = data?.items ?? []

  return {
    ...rest,
    data,
    items,
    swimLevels: items.filter((s) => s.category === "swim_level"),
    locations: items.filter((s) => s.category === "location"),
    programTypes: items.filter((s) => s.category === "program_type"),
  }
}
